import { themeStyle } from '@src/theming';
import { TransientProps } from '@src/utils/transientProps';
import styled, { RuleSet, css } from 'styled-components';

import { STATE_TRANSITION_DURATION } from './constants';
import { ToastProps } from './types';

export const StyledDefaultToast = styled.div<
  TransientProps<Omit<ToastProps, 'theme' | 'children'>> &
    TransientProps<ToastProps['theme']>
>`
  ${({ $height }): RuleSet => {
    if ($height) {
      return css`
        height: ${$height}px;
      `;
    }

    return css`
      min-height: 60px;
      height: auto;
    `;
  }}

  display: flex;

  width: fit-content;

  background: ${({ $background }): string | undefined => $background};
  border-radius: 50px;
  gap: 12px;
  align-items: center;
  box-sizing: border-box;
  padding: 8px 24px 8px 8px;
  color: ${({ $color }): string | undefined => $color};
  box-shadow: ${({ $shadow }): string | undefined => $shadow};
  transition: all ${STATE_TRANSITION_DURATION}ms;

  ${({ $transitionStatus }): RuleSet | undefined => {
    if ($transitionStatus === 'entered') {
      return css`
        opacity: 1;
        transform: translate3d(0, 0, 0);
      `;
    }

    if ($transitionStatus === 'exiting' || $transitionStatus === 'exited') {
      return css`
        opacity: 0;
        transform: translate3d(0, -100%, 0);
      `;
    }

    if ($transitionStatus !== undefined) {
      return css`
        opacity: 0;
        transform: translate3d(0, 100%, 0);
      `;
    }

    return;
  }}

  /* Prevent icon shrinkage with long text */
  & > :first-child {
    flex-shrink: 0;
  }
`;

export const ToastDefaultStage = styled.div`
  display: flex;
  flex-direction: column;

  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  // fix for ui-kit dialog overlapping toast, toast should be on top of everything
  // Todo: restore to the highest z-index in the app when the Dialog component doesn't use arbitrary z-index anymore
  z-index: 999999999;
  gap: 20px;
  align-items: center;
`;

export const StyledLightPopoverToast = styled.div<
  TransientProps<Omit<ToastProps, 'theme' | 'children'>> &
    TransientProps<ToastProps['theme']>
>`
  ${({ $height }): RuleSet => {
    if (typeof $height === 'string') {
      return css`
        min-height: 60px;
        height: auto;
      `;
    }
    return css`
      height: ${$height}px;
    `;
  }}
  display: flex;

  background: ${themeStyle.varBackgroundAlt};
  border-radius: ${themeStyle.radiusMedium};
  padding: 20px;
  align-items: flex-start;
  box-sizing: border-box;
  color: ${themeStyle.varInkMain};
  box-shadow: ${({ $shadow }): string | undefined => $shadow};
  transition: all ${STATE_TRANSITION_DURATION}ms;
  ${({ $transitionStatus }): RuleSet | undefined => {
    if ($transitionStatus === 'entered') {
      return css`
        opacity: 1;
        transform: translate3d(0, 0, 0);
      `;
    }
    if ($transitionStatus === 'exiting' || $transitionStatus === 'exited') {
      return css`
        opacity: 0;
        transform: translate3d(0, -100%, 0);
      `;
    }
    if ($transitionStatus !== undefined) {
      return css`
        opacity: 0;
        transform: translate3d(0, 100%, 0);
      `;
    }
    return;
  }}
  /* Prevent icon shrinkage with long text */
  & > :first-child {
    flex-shrink: 0;
  }
  min-width: 272px;
`;

export const ToastLightPopoverStage = styled.div`
  display: flex;
  flex-direction: column;

  position: fixed;
  bottom: 20px;
  right: 20px;
  // fix for ui-kit dialog overlapping toast, toast should be on top of everything
  // Todo: restore to the highest z-index in the app when the Dialog component doesn't use arbitrary z-index anymore
  z-index: 999999999;
  gap: 20px;
`;
